/**
 * Wire types for the `recurringCharges` module — derived field-by-field from
 * `serializeRecurringCharge` (refactor/backend/src/modules/recurringCharges/serializers.ts).
 * snake_case verbatim, no camelCase mapping layer — see the FIELD-NAMING
 * DECISION note in src/types/user.ts.
 *
 * Node-only feature — no Django counterpart, no Django app. See
 * refactor/docs/PARITY_NOTES.md.
 *
 * MONEY IS A STRING. `amount`/`rate` are quantised strings like `"1000.00"`,
 * never JSON numbers — never do arithmetic on these beyond display formatting.
 */
import type { Invoice } from './payment';

/** `RecurringCharge.scopeMode` (schema.prisma). */
export const RECURRING_CHARGE_SCOPE_MODES = ['LEASE', 'PROPERTY', 'PROPERTY_SHARED'] as const;
export type RecurringChargeScopeMode = (typeof RECURRING_CHARGE_SCOPE_MODES)[number];

/** `RecurringCharge.chargeBasis`. */
export const RECURRING_CHARGE_BASES = ['FLAT', 'METERED'] as const;
export type RecurringChargeBasis = (typeof RECURRING_CHARGE_BASES)[number];

/** Subset of `ChargeType` (types/payment.ts) valid for a recurring charge — no RENT/DEPOSIT/LATE_FEE. */
export const RECURRING_CHARGE_TYPES = ['WATER', 'GARBAGE', 'ELECTRICITY', 'INTERNET', 'OTHER'] as const;
export type RecurringChargeType = (typeof RECURRING_CHARGE_TYPES)[number];

/** Further subset of RECURRING_CHARGE_TYPES valid when charge_basis is METERED. */
export const METERED_CHARGE_TYPES = ['WATER', 'ELECTRICITY'] as const;
export type MeteredChargeType = (typeof METERED_CHARGE_TYPES)[number];

/** `serializeRecurringCharge` — recurringCharges/serializers.ts. */
export interface RecurringCharge {
  id: number;
  scope_mode: RecurringChargeScopeMode;
  charge_basis: RecurringChargeBasis;
  charge_type: RecurringChargeType;
  lease: number | null;
  lease_display: string | null;
  property: number | null;
  property_name: string | null;
  amount: string;
  rate: string | null;
  is_active: boolean;
  notes: string;
  created_at: string;
  updated_at: string;
}

/** `recurringChargeCreateSchema` on write — LEASE requires `lease`, PROPERTY/PROPERTY_SHARED require `property`. */
export interface RecurringChargeCreateRequest {
  scope_mode: RecurringChargeScopeMode;
  charge_basis: RecurringChargeBasis;
  charge_type: RecurringChargeType;
  lease?: number;
  property?: number;
  amount: string;
  rate?: string;
  notes?: string | undefined;
}

/** `recurringChargePatchSchema` — only these four fields are ever mutable after creation. */
export interface RecurringChargePatchRequest {
  amount?: string;
  rate?: string;
  notes?: string;
  is_active?: boolean;
}

/** `POST /:id/record_reading/` body. */
export interface RecordReadingRequest {
  reading: string;
  is_baseline?: boolean;
}

/** `POST /:id/record_reading/` response. `invoice` is null only when `is_baseline: true` was sent. */
export interface RecordReadingResponse {
  invoice: Invoice | null;
  previous_reading: string;
  new_reading: string;
  consumption?: string;
}
